import { getErrorMessage } from '../../utils/errorMessage';
import { useEffect, useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  ArrowDownLeft,
  ArrowUpRight,
  ChevronRight,
  Download,
  Gem,
  History,
  RefreshCw,
  Wallet,
} from 'lucide-react';
import { formatBillingDate, getBillingLogLabel } from './billingData';
import { getCreditLedgerActivity } from './creditLedgerTypes';
import { downloadCreditLedger, saveCreditLedgerExport } from '../../utils/creditAccountExportApi';
import MyPackagesPanel from './MyPackagesPanel';
import { creditOperationStatus, isCreditOperationActive } from './CreditLedgerDetails';
import CreditBalanceAfter from './CreditBalanceAfter';
import { getCreditAccountLedger } from './billingHistoryApi';

const FILTERS = [
  { id: 'all', label: '全部' },
  { id: 'income', label: '收入' },
  { id: 'spend', label: '支出' },
];

export default function CreditLedgerPanel({
  user,
  account,
  logs = [],
  loading,
  error,
  onRetry,
  preview = false,
  english = false,
}) {
  const [filter, setFilter] = useState('all');
  const [scope, setScope] = useState('personal');
  const [page, setPage] = useState(1);
  const [refresh, setRefresh] = useState(0);
  const [liveLedger, setLiveLedger] = useState(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [ledgerError, setLedgerError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [exportFeedback, setExportFeedback] = useState({ type: '', message: '' });
  const wallet = account?.wallet || {};
  const purchasedCredits = Number(wallet.purchased_credits || 0);
  const rewardedCredits = Number(wallet.rewarded_credits || 0);
  const spentCredits = Number(wallet.total_spent_credits || 0);
  const activeScope = scope === 'team' && account?.team_wallet ? 'team' : 'personal';
  const serverView = account?.ledger?.view === 'operations' && !preview;
  const queryKey = `${activeScope}:${filter}:${page}`;
  const currentLedger = liveLedger?.key === queryKey ? liveLedger.data : null;
  const credits = Number((activeScope === 'personal' ? currentLedger?.available_credits : null) ?? wallet.available_credits ?? user?.credits ?? 0);
  const initialLogs = activeScope === 'team'
    ? (Array.isArray(account.team_wallet.ledger) ? account.team_wallet.ledger : [])
    : logs;
  const ledgerLogs = currentLedger?.list || initialLogs;
  const ledgerTotal = Number(currentLedger?.total ?? (activeScope === 'team'
    ? account.team_wallet.ledger_total ?? ledgerLogs.length
    : account?.ledger?.total ?? ledgerLogs.length));
  const pageCount = Math.max(1, Math.ceil(ledgerTotal / 50));
  const busy = loading || (serverView && ledgerLoading);
  const listError = error || (serverView && ledgerError);
  useEffect(() => {
    if (!serverView) return undefined;
    let active = true;
    let timer;
    let inFlight = false;
    const load = async (quiet = false) => {
      if (inFlight) return;
      inFlight = true;
      window.clearTimeout(timer);
      if (!quiet) setLedgerLoading(true);
      setLedgerError('');
      try {
        const data = await getCreditAccountLedger({ scope: activeScope, direction: filter === 'spend' ? 'expense' : filter, page, per_page: 50 });
        if (!active) return;
        setLiveLedger({ key: queryKey, data });
        if (data.page && data.page !== page) setPage(data.page);
        if (data.list?.some(isCreditOperationActive)) timer = window.setTimeout(() => load(true), 10000);
      } catch (cause) {
        if (active) setLedgerError(getErrorMessage(cause, '点数流水暂时无法加载，请重试'));
      } finally {
        inFlight = false;
        if (active) setLedgerLoading(false);
      }
    };
    const onFocus = () => load(true);
    load();
    window.addEventListener('focus', onFocus);
    return () => { active = false; window.clearTimeout(timer); window.removeEventListener('focus', onFocus); };
  }, [serverView, activeScope, filter, page, queryKey, refresh]);
  const metricLabel = value => (loading && !account
    ? '—'
    : value.toLocaleString(english ? 'en-US' : 'zh-CN'));
  const exportLabel = exporting
    ? (english ? 'Generating…' : '正在生成…')
    : exportFeedback.type === 'success'
      ? (english ? 'Downloaded' : '已下载')
      : exportFeedback.type === 'error'
        ? ('导出失败，重试')
        : (english ? 'Export ledger' : '导出流水');
  const filteredLogs = useMemo(() => ledgerLogs.filter(item => {
    const amount = Number(item.amount || 0);
    if (filter === 'income') return amount > 0;
    if (filter === 'spend') return amount < 0;
    return true;
  }), [filter, ledgerLogs]);

  const handleExport = async () => {
    if (preview || exporting) return;
    setExporting(true);
    setExportFeedback({ type: '', message: '' });
    try {
      const result = await downloadCreditLedger({ scope: activeScope });
      saveCreditLedgerExport(result);
      setExportFeedback({
        type: 'success',
        message: english ? 'Downloaded' : '已下载',
      });
    } catch (exportError) {
      setExportFeedback({
        type: 'error',
        message: getErrorMessage(exportError, '导出失败，重试'),
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="account-content__stack">
      <section className="account-page-heading">
        <div>
          <span className="account-eyebrow">CREDIT WALLET</span>
          <h1>{english ? 'Credit Account' : '点数账户'}</h1>
          <p>{english ? 'View your balance, active packages, and recent credit activity.' : '查看余额、有效套餐和最近点数流水。'}</p>
        </div>
        {(error || loading) && <span className="account-status account-status--warm" role="status">
          {english
            ? (error ? '点数账户暂不可用' : 'Syncing credit account')
            : (error ? '点数账户暂不可用' : '正在同步点数账户')}
        </span>}
      </section>

      <section className="billing-ledger-wallet">
        <div className="billing-ledger-wallet__balance">
          <span className="billing-ledger-wallet__icon"><Gem size={20} /></span>
          <div>
            <span>{english ? 'Available personal credits' : '个人可用点数'}</span>
            <strong>{credits.toLocaleString(english ? 'en-US' : 'zh-CN')}</strong>
          </div>
        </div>
        <div className="billing-ledger-wallet__sources">
          <div>
            <span>{english ? 'Purchased (lifetime)' : '累计购买入账'}</span>
            <strong>{metricLabel(purchasedCredits)}</strong>
          </div>
          <div>
            <span>{english ? 'Rewards (lifetime)' : '累计赠送奖励'}</span>
            <strong>{metricLabel(rewardedCredits)}</strong>
          </div>
          <div>
            <span>{english ? 'Spent (lifetime)' : '累计消耗'}</span>
            <strong>{metricLabel(spentCredits)}</strong>
          </div>
        </div>
        <NavLink
          className="billing-primary-link"
          to={preview ? '/prototype/vnext/billing/store' : '/billing/store'}
        >
          {english ? 'Buy credits' : '购买点数'} <ChevronRight size={15} />
        </NavLink>
      </section>

      <MyPackagesPanel preview={preview} english={english} embedded />

      <section className="billing-data-boundary">
        <History size={16} />
        <div>
          <strong>{english ? `${ledgerTotal} entries; ${ledgerLogs.length} on this page` : `共 ${ledgerTotal} 条流水，本页 ${ledgerLogs.length} 条`}</strong>
          <span>{activeScope === 'team'
            ? (english ? `Current team: ${account.team_wallet.team_name}. Includes this team's credit activity.` : `当前团队：${account.team_wallet.team_name}。展示该团队的点数变动。`)
            : (english ? 'Personal credit activity. Switch wallets below to view the current team.' : '当前展示个人点数流水，可在下方切换查看当前团队。')}</span>
        </div>
      </section>

      <section className="billing-ledger">
        <header className="billing-ledger__header">
          <div>
            <span className="account-eyebrow">CREDIT LEDGER</span>
            <h2>{english ? 'Credit Activity' : '点数流水'}</h2>
          </div>
          <button
            type="button"
            className={`billing-export-button${exportFeedback.type ? ` is-${exportFeedback.type}` : ''}`}
            disabled={preview || exporting}
            onClick={handleExport}
            title={preview ? (english ? 'Sample activity cannot be exported' : '示例流水无法导出') : exportFeedback.message}
          >
            <Download className={exporting ? 'account-spin' : ''} size={14} />
            {preview ? (english ? 'Sample · export unavailable' : '示例流水不可导出') : exportLabel}
          </button>
        </header>
        <div className="billing-ledger__scope billing-segmented" role="radiogroup" aria-label={english ? 'Credit ownership' : '点数归属'}>
          {['personal', 'team'].map(value => (
            <button
              type="button"
              role="radio"
              key={value}
              aria-checked={activeScope === value}
              className={activeScope === value ? 'is-active' : ''}
              disabled={loading || exporting || (value === 'team' && !account?.team_wallet)}
              title={value === 'team' && !account?.team_wallet ? (english ? 'No current team wallet' : '当前没有可查看的团队钱包') : undefined}
              onClick={() => { setScope(value); setFilter('all'); setPage(1); setExportFeedback({ type: '', message: '' }); }}
            >
              {value === 'team' ? (english ? 'Team credits' : '团队点数') : (english ? 'Personal credits' : '个人点数')}
            </button>
          ))}
        </div>
        {exportFeedback.message && <p className="billing-ledger__export-feedback" role={exportFeedback.type === 'error' ? 'alert' : 'status'}>{exportFeedback.message}</p>}
        <div className="billing-ledger__toolbar">
          <div className="billing-segmented" role="radiogroup" aria-label={english ? 'Credit direction' : '收支方向'}>
            {FILTERS.map(item => (
              <button
                key={item.id}
                type="button"
                role="radio"
                aria-checked={filter === item.id}
                className={filter === item.id ? 'is-active' : ''}
                onClick={() => { setFilter(item.id); setPage(1); }}
              >
                {english ? ({ all: 'All', income: 'Income', spend: 'Spending' }[item.id]) : item.label}
              </button>
            ))}
          </div>
          {serverView ? <button className="credit-operation-toggle" type="button" disabled={busy} onClick={() => setRefresh(value => value + 1)}><RefreshCw size={13} />{english ? 'Refresh' : '刷新'}</button> : <span>{english ? `${ledgerLogs.length} loaded` : `已加载 ${ledgerLogs.length} 条`}</span>}
        </div>

        {busy ? (
          <div className="billing-ledger__empty">{english ? 'Loading credit activity…' : '正在读取点数流水…'}</div>
        ) : listError ? (
          <div className="billing-ledger__empty is-error" role="alert">
            {ledgerError || '点数流水暂时无法加载，余额和其他账户功能仍可使用。'}
            {serverView && <button type="button" className="credit-operation-toggle" onClick={() => setRefresh(value => value + 1)}>{english ? 'Retry' : '重试'}</button>}
            {!serverView && onRetry && <button type="button" className="credit-operation-toggle" onClick={onRetry}>{english ? 'Retry' : '重试'}</button>}
          </div>
        ) : filteredLogs.length === 0 ? (
          <div className="billing-ledger__empty">
            <Wallet size={22} />
            <strong>{english ? (ledgerLogs.length === 0 ? 'No credit activity yet' : 'No activity in this category') : (ledgerLogs.length === 0 ? '暂无点数变动' : '该分类下暂无记录')}</strong>
            <span>{english ? 'Purchases, rewards, and creation costs will appear here.' : '充值、奖励或创作消耗会显示在这里。'}</span>
          </div>
        ) : (
          <div className="billing-ledger__rows">
            <div className="billing-ledger__columns" aria-hidden="true">
              <span /><span>{english ? 'Details' : '变动明细'}</span><span>{english ? 'Activity type' : '流水类型'}</span><span>{english ? 'Time' : '发生时间'}</span><span>{english ? 'Credits' : '变动点数'}</span><span title={english ? 'Wallet balance after this activity’s last change' : '该笔记录最后一次变动后的钱包余额'}>{english ? 'Remaining credits' : '剩余积分'}</span>
            </div>
            {filteredLogs.map(item => {
              const amount = Number(item.amount || 0);
              const positive = amount > 0;
              const DirectionIcon = positive ? ArrowDownLeft : ArrowUpRight;
              const activity = getCreditLedgerActivity(item, activeScope, english);
              return (
                <article className="billing-ledger-row" key={`${activeScope}:${item.id}`}>
                  <span className={`billing-ledger-row__icon${positive ? ' is-positive' : ''}`}>
                    <DirectionIcon size={15} />
                  </span>
                  <div className="billing-ledger-row__main">
                    <strong>{getBillingLogLabel(item, english ? 'en-US' : 'zh-CN')}</strong>
                    <span>{item.grouped ? creditOperationStatus(item, english) : item.note || (english ? 'No additional details' : '暂无补充说明')}</span>
                  </div>
                  <div className="billing-ledger-row__type" aria-label={`${english ? 'Activity type' : '流水类型'}：${activity.label}，${activity.scopeLabel}`}>
                    <span>{activity.label}</span>
                    <small>{activity.scopeLabel}</small>
                  </div>
                  <time dateTime={item.created_at || undefined}>
                    {formatBillingDate(item.created_at, { locale: english ? 'en-US' : 'zh-CN' })}
                  </time>
                  <b className={positive ? 'is-positive' : ''}>
                    {positive ? '+' : ''}{amount.toLocaleString(english ? 'en-US' : 'zh-CN')} {english ? 'credits' : '点'}
                  </b>
                  <CreditBalanceAfter value={item.balance_after} english={english} compact />
                </article>
              );
            })}
          </div>
        )}
        {serverView && !busy && !listError && pageCount > 1 && <div className="credit-operation-pages" role="group" aria-label={english ? 'Credit activity pages' : '点数流水分页'}>
          <button type="button" disabled={page <= 1} onClick={() => setPage(value => value - 1)}>{english ? 'Previous' : '上一页'}</button>
          <span>{page} / {pageCount}</span>
          <button type="button" disabled={page >= pageCount} onClick={() => setPage(value => value + 1)}>{english ? 'Next' : '下一页'}</button>
        </div>}
      </section>
    </div>
  );
}
